import React, { useEffect, useState } from 'react'
import { Typography, Modal, Button, Card, Spin, Badge, Avatar, Row, Col } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faHeartPulse,
  faTriangleExclamation,
  faFlask,
  faCalendarCheck,
  faChartLine,
  faUserDoctor,
  faVideo,
  faShield
} from '@fortawesome/free-solid-svg-icons'
import Lottie from 'react-lottie'
import { useSelector, useDispatch } from 'react-redux'
import { App as AntApp } from 'antd'
import {
  BellOutlined,
  MedicineBoxOutlined,
  CheckCircleFilled,
  RightOutlined,
  VideoCameraOutlined
} from '@ant-design/icons'

import { dashboardData } from '../../../mockdata/dashboardData'
import { generateAIResponse } from '../../AI CareSync/CareSync Suggest AI/assistants-lib'
import { assistantInitiate } from '../../AI CareSync/CareSync Suggest AI/utils'
import '../../styling/CareSyncOverview.css'
import RecentResults from './components/RecentResults'
import TestPromo from './components/TestPromo'
import ResultDetail from '../Results/ResultDetail/ResultDetail'
import HealthWarnings from './components/HealthWarnings'
import comingSoonAnimation from '../../../../../../assets/Animations/Coming Soon.json'
import InitialModal from '../../AI CareSync/CareSync Suggest AI/InitialModal'
import TestRecommendationModal from '../../AI CareSync/CareSync Suggest AI/TestRecommendationModal'
import PatClinicConsultBooking from '../../../../Clinic Consultation'
import CoreEnums from '../../../../../../core/utils/enums'
import coreReducer from '../../../../../../core/utils/reducer'

const { Text, Title } = Typography

const CareSyncContent = ({
  setActiveTab,
  isLoading: parentLoading,
  onViewDetail,
  onBackFromDetail,
  isViewingDetail
}) => {
  // State
  const { recentResults } = dashboardData
  const [selectedResult, setSelectedResult] = useState(null)
  const [showInitialModal, setShowInitialModal] = useState(false)
  const [showRecommendationModal, setShowRecommendationModal] = useState(false)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [currentWarning, setCurrentWarning] = useState(null)
  const [healthWarnings, setHealthWarnings] = useState([])
  const [recommendation, setRecommendation] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [recommendedTests, setRecommendedTests] = useState([])
  const dispatch = useDispatch()

  // Data
  const userData = useSelector((state) => state.auth?.agiliteUser || {})
  const testResults = useSelector((state) => state.careSyncTestResults?.data || [])

  // Load warnings from localStorage on mount
  useEffect(() => {
    const savedWarnings = localStorage.getItem('healthWarnings')
    if (savedWarnings) {
      setHealthWarnings(JSON.parse(savedWarnings))
    }

    // Load saved recommendations
    const savedTests = localStorage.getItem('recommendedTests')
    if (savedTests) {
      setRecommendedTests(JSON.parse(savedTests))
    }
  }, [])

  // Save warnings when they change
  useEffect(() => {
    if (healthWarnings.length > 0) {
      localStorage.setItem('healthWarnings', JSON.stringify(healthWarnings))
    }
  }, [healthWarnings])

  // Save recommendations when they change
  useEffect(() => {
    if (recommendedTests.length > 0) {
      localStorage.setItem('recommendedTests', JSON.stringify(recommendedTests))
    }
  }, [recommendedTests])

  // Handle opening virtual consultation booking
  const handleOpenVirtualConsult = () => {
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.MY_BOOKINGS}_new_virtual_consult`,
        closable: true,
        label: CoreEnums.tabKeys.MY_BOOKINGS,
        children: <PatClinicConsultBooking isVirtual={true} />
      })
    )
  }

  const addWarning = (warningText) => {
    const newWarning = {
      text: warningText,
      timestamp: Date.now()
    }
    setHealthWarnings((prev) => [...prev, newWarning])
    setCurrentWarning(newWarning)
    setShowWarningModal(true)
  }

  const addNewRecommendation = (test) => {
    // Check if test with same name already exists
    const exists = recommendedTests.some(
      (existingTest) => existingTest.testName.toLowerCase() === test.name.toLowerCase()
    )

    if (!exists) {
      setRecommendedTests((prevTests) => [
        ...prevTests,
        {
          id: Date.now(),
          testName: test.name,
          reason: test.reason,
          priority: 'high',
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          preparation: 'Fasting may be required'
        }
      ])
    }
  }

  const handleAIAnalysis = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const threadData = await assistantInitiate()
      const response = await generateAIResponse(threadData)

      if (response?.data?.content) {
        try {
          let contentString = response.data.content

          // Extract the value from the content object
          if (typeof contentString === 'object' && contentString.value) {
            contentString = contentString.value
          }

          // Clean up any remaining markdown or whitespace
          contentString = contentString
            .replace(/```json\n?/, '')
            .replace(/\n?```/, '')
            .trim()

          // Only try to parse if we have content
          if (!contentString) {
            throw new Error('Empty response from AI')
          }

          let parsedData = null

          // Parse the outer JSON if needed
          if (contentString.includes('"test"')) {
            parsedData = JSON.parse(contentString)

            if (parsedData.test) {
              setRecommendation(parsedData.test)
              addNewRecommendation(parsedData.test)
              setShowInitialModal(false)
              setShowRecommendationModal(true)
            }
          } else {
            // Handle double-wrapped JSON
            const parsedOuter = JSON.parse(contentString)
            if (parsedOuter.value) {
              parsedData = JSON.parse(parsedOuter.value)

              if (parsedData.test) {
                setRecommendation(parsedData.test)
                addNewRecommendation(parsedData.test)
                setShowInitialModal(false)
                setShowRecommendationModal(true)
              }
            }
          }

          // Add warning detection after parsing
          if (parsedData && (parsedData.warning || parsedData.test?.warning)) {
            const warningText = parsedData.warning || parsedData.test.warning
            addWarning(warningText)
          }
        } catch (parseError) {
          console.error('Error parsing AI response:', parseError)
          setError(`Unable to parse AI recommendations: ${parseError.message}`)
        }
      } else {
        console.error('Response structure:', response)
        throw new Error('No content in AI response')
      }
    } catch (error) {
      console.error('AI Analysis error:', error)
      setError(error.message || 'Failed to get AI recommendations')
    } finally {
      setIsLoading(false)
    }
  }

  // View test result details
  const handleViewResult = (result) => {
    setSelectedResult(result)
    if (onViewDetail) {
      onViewDetail()
    }
  }

  // Handle back from result detail
  const handleBackFromDetail = () => {
    setSelectedResult(null)
    if (onBackFromDetail) {
      onBackFromDetail()
    }
  }

  // Animation options
  const comingSoonOptions = {
    loop: true,
    autoplay: true,
    animationData: comingSoonAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  }

  // Render result detail view
  if (selectedResult) {
    return <ResultDetail result={selectedResult} onBack={handleBackFromDetail} />
  }

  // Calculate test stats
  const resultsCount = testResults?.length || 0

  return (
    <div className='cs-overview-container'>
      {(isLoading || parentLoading) && (
        <div className='cs-overview-loading'>
          <Spin size='large' tip='Loading your health data...' />
        </div>
      )}

      {/* User Greeting & Summary */}
      <section className='cs-overview-header'>
        <div className='cs-user-info'>
          <Avatar
            className='cs-user-avatar'
            src={userData.profilePicture}
            icon={!userData.profilePicture && <FontAwesomeIcon icon={faUserDoctor} />}
          >
            {userData.firstName?.charAt(0)}
          </Avatar>
          <div className='cs-user-greeting'>
            <Title level={4}>Hi, {userData.firstName || 'There'}!</Title>
            <Text className='cs-greeting-subtitle'>Welcome to your health dashboard</Text>
          </div>
        </div>
        <Badge count={healthWarnings.length} className='cs-notification-badge'>
          <Button className='cs-notification-button' icon={<BellOutlined />} shape='circle' />
        </Badge>
      </section>

      {/* Test Stats Card */}
      <section className='cs-test-stats'>
        <Card className='cs-stats-card'>
          <div className='cs-stats-card-inner'>
            <div className='cs-stats-card-icon'>
              <FontAwesomeIcon icon={faFlask} />
            </div>
            <div className='cs-stats-card-content'>
              <Text className='cs-stats-card-label'>Recent Tests</Text>
              <Text className='cs-stats-card-value'>{resultsCount}</Text>
            </div>
          </div>
        </Card>
      </section>

      {/* Quick Actions */}
      <section className='cs-quick-actions'>
        <Title level={5}>Quick Actions</Title>
        <div className='cs-action-buttons'>
          <Button
            className='cs-action-button cs-action-button--primary'
            icon={<FontAwesomeIcon icon={faCalendarCheck} />}
            onClick={() => setActiveTab('tests')}
          >
            Book Test
          </Button>
          <Button
            className='cs-action-button cs-action-button--secondary'
            icon={<VideoCameraOutlined />}
            onClick={handleOpenVirtualConsult}
          >
            Virtual Consult
          </Button>
          <Button
            className='cs-action-button cs-action-button--tertiary'
            icon={<MedicineBoxOutlined />}
            onClick={() => setActiveTab('results')}
          >
            Results
          </Button>
        </div>
      </section>

      {/* Health Warnings */}
      {healthWarnings.length > 0 && (
        <section className='cs-warnings-section'>
          <HealthWarnings warnings={healthWarnings} />
        </section>
      )}

      {/* Test Promotions */}
      <section className='cs-section'>
        <TestPromo onViewAll={(tabKey) => setActiveTab(tabKey)} onBookTest={(tabKey) => setActiveTab(tabKey)} />
      </section>

      {/* Recent Results */}
      <section className='cs-section'>
        <RecentResults results={recentResults} onViewDetails={handleViewResult} />
      </section>

      {/* AI Health Assistant */}
      <section className='cs-ai-health-assistant'>
        <div className='cs-assistant-header'>
          <Title level={5}>AI Health Assistant</Title>
          <div className='cs-assistant-badge'>
            <CheckCircleFilled /> Beta
          </div>
        </div>
        <Card className='cs-assistant-card' onClick={() => setShowInitialModal(true)}>
          <div className='cs-assistant-content'>
            <div className='cs-assistant-icon'>
              <FontAwesomeIcon icon={faShield} />
            </div>
            <div className='cs-assistant-text'>
              <Text className='cs-assistant-title'>Get Health Recommendations</Text>
              <Text className='cs-assistant-description'>
                Our AI can analyze your health data and suggest appropriate tests
              </Text>
            </div>
            <Button type='link' className='cs-assistant-action' icon={<RightOutlined />} />
          </div>
        </Card>
      </section>

      {/* Health Reminders */}
      <section className='cs-health-reminders'>
        <Card className='cs-reminder-card'>
          <div className='cs-reminder-content'>
            <div className='cs-reminder-icon'>
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </div>
            <div className='cs-reminder-text'>
              <Text className='cs-reminder-title'>Remember</Text>
              <Text className='cs-reminder-description'>
                Regular health check-ups are essential for early detection
              </Text>
            </div>
            <Button type='link' className='cs-reminder-action' icon={<RightOutlined />} />
          </div>
        </Card>
      </section>

      {/* Modals */}
      <InitialModal
        isVisible={showInitialModal}
        onClose={() => {
          setShowInitialModal(false)
          setError(null)
        }}
        onProceed={handleAIAnalysis}
        isLoading={isLoading}
        error={error}
      />

      <TestRecommendationModal
        isVisible={showRecommendationModal}
        onClose={() => setShowRecommendationModal(false)}
        recommendation={recommendation}
      />

      <Modal
        title={
          <span className='cs-warning-modal-title'>
            <FontAwesomeIcon icon={faTriangleExclamation} />
            Health Warning
          </span>
        }
        open={showWarningModal}
        onOk={() => setShowWarningModal(false)}
        onCancel={() => setShowWarningModal(false)}
        okText='Got it'
        cancelButtonProps={{ style: { display: 'none' } }}
        className='cs-warning-modal'
      >
        <Text strong className='cs-warning-modal-text'>
          {currentWarning?.text}
        </Text>
      </Modal>
    </div>
  )
}

// Create a wrapper component that provides the App context
const CareSyncOverview = (props) => {
  return (
    <AntApp>
      <CareSyncContent {...props} />
    </AntApp>
  )
}

export default CareSyncOverview
